import BaseButton from '../BaseButton';
import {
  cloneElement,
  forwardRef,
  isValidElement,
  type ButtonHTMLAttributes,
  type ReactElement
} from 'react';
import { IconProps } from 'interfaces';

type ActionBarActionVariant = 'icon' | 'text' | 'icon-text';
type ActionBarActionSize = 'small' | 'medium' | 'large';

interface ActionBarActionProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  icon?: JSX.Element;
  label?: string;
  variant?: ActionBarActionVariant;
  size?: ActionBarActionSize;
  className?: string;
  'data-testid'?: string;
}

const ActionBarAction = forwardRef<HTMLButtonElement, ActionBarActionProps>(
  (
    {
      icon,
      label,
      variant = 'icon',
      size = 'medium',
      onClick,
      disabled = false,
      className = '',
      'aria-label': ariaLabel,
      'data-testid': testId,
      ...rest
    },
    ref
  ) => {
    const sizeClasses = {
      small: {
        icon: '~w-3.5/4.5 ~h-3.5/4.5',
        text: '~text-[0.625rem]/xs'
      },
      medium: {
        icon: '~w-4/5 ~h-4/5',
        text: '~text-xs/sm'
      },
      large: {
        icon: '~w-5/6 ~h-5/6',
        text: '~text-sm/base'
      }
    };

    const currentSize = sizeClasses[size];

    if (variant === 'icon' && icon) {
      const iconWithSize = isValidElement(icon)
        ? cloneElement(icon as ReactElement<IconProps>, {
            styles: `${currentSize.icon} ${(icon.props as IconProps).styles || ''}`,
            ...(icon.props as IconProps)
          })
        : icon;

      return (
        <BaseButton
          ref={ref}
          icon={iconWithSize}
          variant='icon'
          onClick={onClick}
          disabled={disabled}
          additionalStyles={className}
          aria-label={ariaLabel || label}
          data-testid={testId}
          size='xs'
          {...rest}
        />
      );
    }

    if (variant === 'text' && label) {
      return (
        <BaseButton
          ref={ref}
          title={label}
          variant='primary'
          onClick={onClick}
          disabled={disabled}
          additionalStyles={className}
          aria-label={ariaLabel || label}
          data-testid={testId}
          size='xs'
          {...rest}
        />
      );
    }

    if (variant === 'icon-text' && icon && label) {
      const iconWithSize = isValidElement(icon)
        ? cloneElement(icon as ReactElement<IconProps>, {
            styles: `${currentSize.icon} ${(icon.props as IconProps).styles || ''}`,
            ...(icon.props as IconProps)
          })
        : icon;

      return (
        <BaseButton
          ref={ref}
          icon={iconWithSize}
          title={label}
          variant='secondary'
          onClick={onClick}
          disabled={disabled}
          additionalStyles={className}
          aria-label={ariaLabel || label}
          data-testid={testId}
          size='xs'
          {...rest}
        />
      );
    }

    return null;
  }
);

ActionBarAction.displayName = 'ActionBarAction';

export default ActionBarAction;
